import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, useMatch } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ActionButton, TabItem } from '../../components/LabsAppBar';
import LabsAppBar from '../../components/LabsAppBar';
import { useNotifications } from '../../contexts/NotificationContext';
import ContractDetailsTab from '../../components/contracts/ContractDetailsTab';
import DataNormalizationTab from '../../components/contracts/DataNormalizationTab';
import ConfirmDialog from '../../components/dialogs/ConfirmDialog';
import { ContractWizardState, ContractStatus, ContractProperty, ContractEvent, ContractPropertyTreeNode } from '../../types/contract';
import { getContract, updateContract, createContract } from '../../services/contractApiService';
import { createNewContractProperty, updateContractPropertyInTree, deleteContractPropertyFromTree } from '../../utils/contractUtils';

// Helper to build tree from flat properties list
const buildTree = (properties: ContractProperty[]): ContractPropertyTreeNode[] => {
  const nodes: Record<string, ContractPropertyTreeNode> = {};
  properties.forEach(p => {
    nodes[p.id] = { ...p, children: [], hasChildren: false, isExpanded: false, level: 0, isLastChild: false }; // Initialize hasChildren, isExpanded, level, and isLastChild
  });

  const tree: ContractPropertyTreeNode[] = [];
  properties.forEach(p => {
    // NOTE: 'parentId' is assumed to exist on ContractProperty for tree building.
    // If ContractProperty type does not include parentId, it needs to be updated in src/types/contract.ts
    if ((p as any).parentId && nodes[(p as any).parentId]) {
      nodes[(p as any).parentId].children.push(nodes[p.id]);
      nodes[(p as any).parentId].hasChildren = true; // Mark parent as having children
      nodes[p.id].level = nodes[(p as any).parentId].level + 1; // Set level for child
    } else {
      tree.push(nodes[p.id]);
    }
  });
  return tree;
};

// Helper to flatten tree to a list for API calls
const flattenTree = (nodes: ContractPropertyTreeNode[]): ContractProperty[] => {
  const flat: ContractProperty[] = [];
  const recurse = (node: ContractPropertyTreeNode) => {
    // Explicitly omit tree-specific properties not present in ContractProperty
    const { children, hasChildren, isExpanded, level, isLastChild, ...rest } = node;
    flat.push(rest as ContractProperty);
    children.forEach(recurse);
  };
  nodes.forEach(recurse);
  return flat;
};

// Helper to find a property recursively in a tree
const findPropertyInTree = (nodes: ContractPropertyTreeNode[], id: string): ContractPropertyTreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findPropertyInTree(node.children, id);
    if (found) return found;
  }
  return null;
};


const ContractEditPage: React.FC = () => {
  const { showNotification } = useNotifications();
  const navigate = useNavigate();
  const { contractId } = useParams<{ contractId: string }>();
  const location = useLocation();
  const { newContract: isNewContractCreation } = (location.state || {}) as { newContract?: boolean };

  const matchDetails = useMatch(`/labs/contracts/:contractId/edit/details`);
  const matchNormalization = useMatch(`/labs/contracts/:contractId/edit/normalization`);

  const [contractState, setContractState] = useState<ContractWizardState>(() => ({
    contractId: contractId || uuidv4(),
    name: '',
    description: '',
    selectedSchemaIds: [],
    selectedIntegrationIds: [],
    events: [],
    allContractProperties: [], // Initialize allContractProperties
    normalizedProperties: {},
    mappingProgress: {
      total: 0,
      mapped: 0,
      required: 0,
      requiredMapped: 0,
    },
    currentStep: 0,
    isSubmitting: false,
    status: 'draft',
    isNewContract: isNewContractCreation || false, // Initialize isNewContract
    customerName: '', // Initialize customerName
    customerEmail: '', // Initialize customerEmail
  }));

 const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
 const [isPublishing, setIsPublishing] = useState(false);
  const initialContractStateRef = useRef<ContractWizardState | null>(null);
  const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false);

  // Keep track of the currently selected event for property management
  const [selectedEvent, setSelectedEvent] = useState<ContractEvent | null>(
    contractState.events.length > 0 ? contractState.events[0] : null
  );

  useEffect(() => {
    // Set initial state after first load or new contract creation
    if (!initialContractStateRef.current && !contractState.isSubmitting) {
      initialContractStateRef.current = contractState;
    }
    // Compare current state with initial state to determine unsaved changes
    const isChanged = JSON.stringify(contractState) !== JSON.stringify(initialContractStateRef.current);
    setHasUnsavedChanges(isChanged);
  }, [contractState]);

  const updateContractState = useCallback((updates: Partial<ContractWizardState>) => {
    setContractState(prevState => ({ ...prevState, ...updates }));
  }, []);

  const handleSaveChanges = useCallback(async (status: ContractStatus = 'draft') => {
    updateContractState({ isSubmitting: true });
    try {
      const payload = {
        name: contractState.name,
        status: status,
        data: {
          description: contractState.description,
          schemaIds: contractState.selectedSchemaIds,
          integrationIds: contractState.selectedIntegrationIds,
          events: contractState.events,
          allContractProperties: flattenTree(contractState.allContractProperties as ContractPropertyTreeNode[]),
          normalizedProperties: contractState.normalizedProperties,
          mappingProgress: contractState.mappingProgress,
          customerName: contractState.customerName,
          customerEmail: contractState.customerEmail,
        },
      };
      
      // Use createContract for new contracts, updateContract for existing ones
      if (contractState.isNewContract || contractId === 'new') {
        const createPayload = {
          schema_id: contractState.selectedSchemaIds[0] || 'default',
          name: contractState.name,
          data: payload.data,
        };
        const newContract = await createContract(createPayload);
        updateContractState({
          contractId: newContract.id,
          isNewContract: false,
          shareToken: newContract.share_token
        });
        showNotification('success', 'New contract created successfully!');
        navigate(`/labs/contracts/${newContract.id}/edit/details`, { replace: true });
        // After creating a new contract, update initial state with the new contract details
        initialContractStateRef.current = {
          ...contractState,
          contractId: newContract.id,
          isNewContract: false,
          shareToken: newContract.share_token,
          status: status,
        };
      } else {
        await updateContract(contractState.contractId!, payload);
        showNotification('success', 'Contract changes saved successfully!');
        if (isNewContractCreation) {
          navigate(`/labs/contracts/${contractState.contractId}/edit/details`, { replace: true });
        }
        // After updating an existing contract, update initial state
        initialContractStateRef.current = { ...contractState, status: status };
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save contract changes:', error);
      showNotification('error', 'Failed to save contract changes. Please try again.');
    } finally {
      updateContractState({ isSubmitting: false });
    }
  }, [contractState, updateContractState, showNotification, isNewContractCreation, navigate]);

  const handlePublishContract = useCallback(async () => {
    setIsPublishing(true);
    updateContractState({ isSubmitting: true }); // Keep isSubmitting true during publish
    try {
      const payload = {
        name: contractState.name,
        status: 'pending' as ContractStatus, // Set status to 'pending' when publishing
        data: {
          description: contractState.description,
          schemaIds: contractState.selectedSchemaIds,
          integrationIds: contractState.selectedIntegrationIds,
          events: contractState.events,
          allContractProperties: flattenTree(contractState.allContractProperties as ContractPropertyTreeNode[]),
          normalizedProperties: contractState.normalizedProperties,
          mappingProgress: contractState.mappingProgress,
          customerName: contractState.customerName,
          customerEmail: contractState.customerEmail,
        },
      };

      // If it's a new contract or contractId is 'new', create it first
      if (contractState.isNewContract || contractId === 'new') {
        const createPayload = {
          schema_id: contractState.selectedSchemaIds[0] || 'default',
          name: contractState.name,
          data: payload.data,
        };
        const newContract = await createContract(createPayload);
        updateContractState({
          contractId: newContract.id,
          isNewContract: false,
          shareToken: newContract.share_token,
          status: 'draft' // Changed from 'pending' to 'draft'
        });
        initialContractStateRef.current = {
          ...contractState,
          contractId: newContract.id,
          isNewContract: false,
          shareToken: newContract.share_token,
          status: 'draft', // Changed from 'pending' to 'draft'
        };
      } else {
        await updateContract(contractState.contractId!, payload);
        initialContractStateRef.current = { ...contractState, status: 'pending' }; // Update initial state with new status
      }
      showNotification('success', 'Contract published successfully!');
      setIsPublishing(false);
      setHasUnsavedChanges(false);
      navigate('/labs/contracts');
    } catch (error) {
      console.error('Failed to publish contract:', error);
      showNotification('error', 'Failed to publish contract. Please try again.');
      setIsPublishing(false);
    } finally {
      updateContractState({ isSubmitting: false });
    }
  }, [contractState, updateContractState, showNotification, navigate]);

  useEffect(() => {
    const loadContractData = async () => {
      if (contractId && contractId !== 'new') {
        try {
          const fetchedContract = await getContract(contractId);

          const events: ContractEvent[] = fetchedContract.data?.events || [];
          const allContractProperties: ContractProperty[] = fetchedContract.data?.allContractProperties || [];

          const totalEventCount = events.length;
          const propertyUsageMap: { [propertyId: string]: number } = {};

          // Calculate event usage for each property
          events.forEach(event => {
            event.properties.forEach(prop => {
              propertyUsageMap[prop.id] = (propertyUsageMap[prop.id] || 0) + 1;
            });
          });

          // Update allContractProperties with usage counts
          const updatedAllContractPropertiesFlat = allContractProperties.map(prop => ({
            ...prop,
            eventUsageCount: propertyUsageMap[prop.id] || 0,
            totalEventCount: totalEventCount,
          }));

          const allContractPropertiesTree = buildTree(updatedAllContractPropertiesFlat);

          // Ensure updatedEventProperties are ContractPropertyTreeNode[] if ContractEvent.properties expects it
          const updatedEvents = events.map(event => {
            const updatedEventProperties: ContractPropertyTreeNode[] = event.properties.map(prop => {
              const fullPropDetail = updatedAllContractPropertiesFlat.find(p => p.id === prop.id);
              // Convert ContractProperty to ContractPropertyTreeNode if necessary
              return {
                id: (fullPropDetail || prop).id,
                name: (fullPropDetail || prop).name,
                path: (fullPropDetail || prop).path,
                codeName: (fullPropDetail || prop).codeName,
                dataType: (fullPropDetail || prop).dataType,
                description: (fullPropDetail || prop).description,
                isRequired: (fullPropDetail || prop).isRequired,
                isPII: (fullPropDetail || prop).isPII,
                friendlyName: (fullPropDetail || prop).friendlyName,
                isNormalized: (fullPropDetail || prop).isNormalized,
                parentId: (fullPropDetail || prop as any).parentId, // Assuming parentId is on ContractProperty
                eventUsageCount: (fullPropDetail || prop).eventUsageCount || 0,
                totalEventCount: (fullPropDetail || prop).totalEventCount || 0,
                children: [], // Ensure children is always an array
                hasChildren: false, // Ensure hasChildren is always present
                isExpanded: false, // Ensure isExpanded is always present
                isLastChild: false, // Ensure isLastChild is always present
                level: 0, // Ensure level is always present (will be updated by buildTree if part of a tree)
              } as ContractPropertyTreeNode;
            });
            return { ...event, properties: updatedEventProperties };
          });

          const loadedState: ContractWizardState = {
            contractId: fetchedContract.id,
            name: fetchedContract.name,
            description: fetchedContract.description || '',
            selectedSchemaIds: fetchedContract.data?.schemaIds || [],
            selectedIntegrationIds: fetchedContract.data?.integrationIds || [],
            events: updatedEvents, // Use the updated events
            allContractProperties: allContractPropertiesTree, // Use the tree
            normalizedProperties: fetchedContract.data?.normalizedProperties || {},
            mappingProgress: fetchedContract.data?.mappingProgress || {
              total: 0,
              mapped: 0,
              required: 0,
              requiredMapped: 0,
            },
            currentStep: 0,
            isSubmitting: false,
            status: fetchedContract.status,
            isNewContract: false,
            shareToken: fetchedContract.share_token,
            customerName: fetchedContract.data?.customerName,
            customerEmail: fetchedContract.data?.customerEmail,
          };
          setContractState(loadedState);
          initialContractStateRef.current = loadedState;
          setHasUnsavedChanges(false);
        } catch (err) {
          showNotification('error', 'Failed to load contract. Please try again.');
        }
      } else if (isNewContractCreation) {
        setContractState(prev => ({
          ...prev,
          contractId: uuidv4(),
          name: '',
          description: '',
          status: 'draft',
          isNewContract: true,
          allContractProperties: [], // No events yet, so counts will be 0
          events: [], // Ensure events is also initialized
        }));
      }
    };

    loadContractData();
  }, [contractId, isNewContractCreation, showNotification]);

  // Update selectedEvent when contractState.events changes
  useEffect(() => {
    if (selectedEvent) {
      const updatedSelectedEvent = contractState.events.find(e => e.id === selectedEvent.id);
      if (updatedSelectedEvent && updatedSelectedEvent !== selectedEvent) {
        setSelectedEvent(updatedSelectedEvent);
      }
    } else if (contractState.events.length > 0) {
      setSelectedEvent(contractState.events[0]);
    }
  }, [contractState.events, selectedEvent]);

  // Recalculate event usage counts when events or properties change
  useEffect(() => {
    const totalEventCount = contractState.events.length;
    const propertyUsageMap: { [propertyId: string]: number } = {};

    contractState.events.forEach(event => {
      if (Array.isArray(event.properties)) {
        event.properties.forEach(prop => {
          if (prop && prop.id) {
            propertyUsageMap[prop.id] = (propertyUsageMap[prop.id] || 0) + 1;
          }
        });
      }
    });

    const updateUsageInTree = (nodes: ContractPropertyTreeNode[]): ContractPropertyTreeNode[] => {
      return nodes.map(prop => {
        const newProp: ContractPropertyTreeNode = {
          ...prop,
          eventUsageCount: propertyUsageMap[prop.id] || 0,
          totalEventCount: totalEventCount,
        };
        if (prop.children) {
          newProp.children = updateUsageInTree(prop.children);
        }
        return newProp;
      });
    };

    const updatedAllContractProperties = updateUsageInTree(contractState.allContractProperties as ContractPropertyTreeNode[]);

    // Only update state if there's a change to avoid infinite loops
    if (JSON.stringify(updatedAllContractProperties) !== JSON.stringify(contractState.allContractProperties)) {
      updateContractState({ allContractProperties: updatedAllContractProperties });
    }
  }, [contractState.events, contractState.allContractProperties, updateContractState]);


  const handleAddProperty = useCallback((eventId: string, parentProperty: ContractProperty | null) => {
    setContractState(prevState => {
      if (!prevState.contractId) return prevState;

      const createdProperty = createNewContractProperty(
        (selectedEvent && selectedEvent.id === eventId) ? 'eventSpecific' : 'global',
        parentProperty
      );

      // Ensure the new property is a ContractPropertyTreeNode
      const newProperty: ContractPropertyTreeNode = {
        ...createdProperty,
        children: [],
        hasChildren: false,
        isExpanded: false,
        isLastChild: false,
        level: parentProperty ? (parentProperty as ContractPropertyTreeNode).level + 1 : 0,
      };

      const addPropertyToParent = (properties: ContractPropertyTreeNode[], parentId: string): ContractPropertyTreeNode[] => {
        return properties.map(p => {
          if (p.id === parentId) {
            return { ...p, children: [...p.children, newProperty], hasChildren: true }; // Mark parent as having children
          }
          return { ...p, children: addPropertyToParent(p.children, parentId) };
        });
      };

      let updatedAllContractPropertiesTree: ContractPropertyTreeNode[];

      if (parentProperty) {
        updatedAllContractPropertiesTree = addPropertyToParent(
          prevState.allContractProperties as ContractPropertyTreeNode[],
          parentProperty.id
        );
      } else {
        updatedAllContractPropertiesTree = [...prevState.allContractProperties, newProperty];
      }

      const updatedNormalizedProperties = {
        ...prevState.normalizedProperties,
        [newProperty.id]: newProperty,
      };

      const updatedEvents = prevState.events.map(event => {
        if (event.id === eventId) {
          return {
            ...event,
            properties: [...event.properties, newProperty],
          };
        }
        return event;
      });

      return {
        ...prevState,
        events: updatedEvents,
        allContractProperties: updatedAllContractPropertiesTree,
        normalizedProperties: updatedNormalizedProperties,
      };
    });
    setHasUnsavedChanges(true);
  }, [selectedEvent]);

  const handleUpdateProperty = useCallback((propertyId: string, updates: Partial<ContractProperty>) => {
    setContractState(prevState => {
      // Update in allContractProperties (tree structure)
      const updatedAllContractPropertiesTree = updateContractPropertyInTree(
        prevState.allContractProperties as ContractPropertyTreeNode[],
        propertyId,
        updates
      );

      // Find the updated property from the tree result
      const updatedProperty = findPropertyInTree(updatedAllContractPropertiesTree, propertyId);
      if (!updatedProperty) return prevState;

      // Update in normalizedProperties map
      const updatedNormalizedProperties = {
        ...prevState.normalizedProperties,
        [propertyId]: {
          ...prevState.normalizedProperties[propertyId],
          ...updates,
          // Explicitly pick properties that belong to ContractProperty from updatedProperty
          id: updatedProperty.id,
          name: updatedProperty.name,
          path: updatedProperty.path,
          codeName: updatedProperty.codeName,
          dataType: updatedProperty.dataType,
          description: updatedProperty.description,
          isRequired: updatedProperty.isRequired,
          isPII: updatedProperty.isPII,
          friendlyName: updatedProperty.friendlyName,
          isNormalized: updatedProperty.isNormalized,
          // Assuming parentId is part of ContractProperty for consistency with buildTree
          parentId: (updatedProperty as any).parentId,
          // These are from ContractPropertyTreeNode but might be needed for some ContractProperty logic
          eventUsageCount: updatedProperty.eventUsageCount,
          totalEventCount: updatedProperty.totalEventCount,
        } as ContractProperty, // Cast to ContractProperty
      };

      // Update event's properties if the path changed
      const updatedEvents = prevState.events.map(event => {
        const updatedEventProperties: ContractPropertyTreeNode[] = event.properties.map(prop => { // Explicitly type here
          if (prop.id === updatedProperty.id) { // Use updatedProperty.id for comparison
            // Return the updatedProperty as a ContractPropertyTreeNode
            return updatedProperty;
          }
          // Convert existing prop to ContractPropertyTreeNode if it's not already
          return {
            id: prop.id,
            name: prop.name,
            path: prop.path,
            codeName: prop.codeName,
            dataType: prop.dataType,
            description: prop.description,
            isRequired: prop.isRequired,
            isPII: prop.isPII,
            friendlyName: prop.friendlyName,
            isNormalized: prop.isNormalized,
            parentId: (prop as any).parentId,
            eventUsageCount: prop.eventUsageCount || 0,
            totalEventCount: prop.totalEventCount || 0,
            children: (prop as ContractPropertyTreeNode).children,
            hasChildren: (prop as ContractPropertyTreeNode).hasChildren,
            isExpanded: (prop as ContractPropertyTreeNode).isExpanded,
            isLastChild: (prop as ContractPropertyTreeNode).isLastChild,
            level: (prop as ContractPropertyTreeNode).level,
          } as ContractPropertyTreeNode;
        });
        return { ...event, properties: updatedEventProperties };
      });

      return {
        ...prevState,
        allContractProperties: updatedAllContractPropertiesTree,
        normalizedProperties: updatedNormalizedProperties,
        events: updatedEvents,
      };
    });
    setHasUnsavedChanges(true);
  }, []);

  const handleDeleteProperty = useCallback((propertyId: string) => {
    setContractState(prevState => {
      const propertyToDelete = findPropertyInTree(prevState.allContractProperties as ContractPropertyTreeNode[], propertyId);
      if (!propertyToDelete) return prevState;

      const idsToDelete: string[] = [];
      const collectIdsToDelete = (prop: ContractPropertyTreeNode) => {
        idsToDelete.push(prop.id);
        if (prop.children) {
          prop.children.forEach(collectIdsToDelete);
        }
      };
      collectIdsToDelete(propertyToDelete);

      const updatedAllContractProperties = deleteContractPropertyFromTree(
        prevState.allContractProperties as ContractPropertyTreeNode[],
        propertyId
      );

      const updatedNormalizedProperties = { ...prevState.normalizedProperties };
      idsToDelete.forEach(id => {
        delete updatedNormalizedProperties[id];
      });

      const updatedEvents = prevState.events.map(event => ({
        ...event,
        properties: event.properties.filter(prop => !idsToDelete.includes(prop.id)),
      }));

      return {
        ...prevState,
        allContractProperties: updatedAllContractProperties,
        normalizedProperties: updatedNormalizedProperties,
        events: updatedEvents,
      };
    });
    setHasUnsavedChanges(true);
  }, []);

  const breadcrumbs = useMemo(() => [
    { name: 'Data Contracts', iconName: 'SquareCheckBig', path: '/labs/contracts' },
    { name: contractState.name || 'New Contract' },
  ], [contractState.name]);

  const tabs: TabItem[] = useMemo(() => [
    { label: 'Contract Details', path: `/labs/contracts/${contractId}/edit/details`, iconName: 'Pencil' },
    { label: 'Data Normalization', path: `/labs/contracts/${contractId}/edit/normalization` },
  ], [contractId]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmCancelDialog(true);
    } else {
      navigate('/labs/contracts');
    }
  }, [hasUnsavedChanges, navigate]);

  const confirmCancel = useCallback(() => {
    navigate('/labs/contracts');
  }, [navigate]);

  const actions: ActionButton[] = useMemo(() => {
    const saveButtonLabel = contractState.isNewContract ? 'Save as Draft' : 'Save Changes';
    const saveButtonLoading = contractState.isSubmitting && !isPublishing;

    const publishButtonDisabled =
      isPublishing ||
      !hasUnsavedChanges ||
      contractState.mappingProgress.requiredMapped < contractState.mappingProgress.required ||
      contractState.allContractProperties.length === 0 ||
      !contractState.name ||
      contractState.selectedSchemaIds.length === 0;

    const commonActions: ActionButton[] = [
      { label: 'Back', onClick: handleCancel, variant: 'text' },
      {
        label: saveButtonLoading ? 'Saving...' : saveButtonLabel,
        onClick: () => handleSaveChanges('draft'),
        variant: 'secondary',
        loading: saveButtonLoading,
        disabled: saveButtonLoading || !hasUnsavedChanges,
      },
    ];

    if (contractState.status === 'draft') {
      commonActions.push({
        label: isPublishing ? 'Publishing...' : 'Publish Contract',
        onClick: handlePublishContract,
        variant: 'primary',
        loading: isPublishing,
        disabled: publishButtonDisabled,
      });
    } else if (contractState.status === 'published') {
      commonActions.push(
        {
          label: 'Save as New Draft',
          onClick: () => showNotification('info', 'Not implemented yet.'),
          variant: 'secondary',
          disabled: true,
        },
        {
          label: 'Save as New Published',
          onClick: () => showNotification('info', 'Not implemented yet.'),
          variant: 'primary',
          disabled: true,
        }
      );
    }

    return commonActions;
  }, [
    handleCancel,
    handleSaveChanges,
    handlePublishContract,
    showNotification,
    contractState.isSubmitting,
    contractState.isNewContract,
    contractState.status,
    contractState.mappingProgress.requiredMapped,
    contractState.mappingProgress.required,
    hasUnsavedChanges,
    isPublishing,
  ]);

  const activeTab = useMemo(() => {
    let tab = 'details';
    if (matchNormalization) {
      tab = 'normalization';
    } else if (matchDetails) {
      tab = 'details';
    }
    return tab;
  }, [matchDetails, matchNormalization, location.pathname]);

  return (
    <div className="flex flex-col h-screen">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
        variant={activeTab === 'normalization' ? 'progress' : 'default'}
        tabs={tabs}
        progress={activeTab === 'normalization' ? {
          current: contractState.mappingProgress.mapped,
          total: contractState.mappingProgress.total,
          label: 'Mapping Progress'
        } : undefined}
        currentSchema={null}
        onSchemaRenameConfirm={undefined}
      />
      <div className="flex-grow overflow-auto bg-gray-50 dark:bg-gray-900">
        {activeTab === 'details' && (
          <ContractDetailsTab state={contractState} updateState={updateContractState} />
        )}
        {activeTab === 'normalization' && (
          <DataNormalizationTab
            state={contractState}
            updateState={updateContractState}
            selectedEvent={selectedEvent}
            onEventSelect={setSelectedEvent}
            handleAddProperty={handleAddProperty}
            handleUpdateProperty={handleUpdateProperty}
            handleDeleteProperty={handleDeleteProperty}
          />
        )}
      </div>

      {showConfirmCancelDialog && (
        <ConfirmDialog
          onClose={() => setShowConfirmCancelDialog(false)}
          title="Discard Unsaved Changes?"
          message="You have unsaved changes. Are you sure you want to discard them and cancel?"
          onConfirm={() => {
            confirmCancel();
          }}
        />
      )}
    </div>
  );
};

export default ContractEditPage;
